import React, { useState, useEffect, useMemo } from 'react'

import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	MenuItem,
	TextField,
	Typography
} from '@material-ui/core'

import { makeStyles } from '@material-ui/core/styles'

import NumberFormat from 'react-number-format'

import { useSelector, useDispatch } from 'react-redux'
import { showError, createItem } from '../../../actions/misc/misc.actions'
import StyledSelect from '../../Common/StyledSelect'
import useSelectedHousehold from '../../../hooks/useSelectedHousehold'
import useCurrentUser from '../../../hooks/useCurrentUser'
import { canAccessFacility } from '../../../utils/staffFacility'
import StyledAlert from '../../Common/StyledAlert'

const useStyles = makeStyles((theme) => ({
	title: {
		marginBottom: 24
	},
	dialogActions: {
		justifyContent: 'flex-start'
	},
	fieldWrapper: {
		minWidth: '400px !important',
		width: '400px !important',
		marginBottom: 20,
		'& .MuiFilledInput-root': {
			minWidth: '400px !important',
			width: '400px !important'
		},
		'& .MuiTextField-root': {
			minWidth: '400px !important',
			width: '400px !important'
		}
	}
}))

function NumberFormatCustom(props) {
	const { inputRef, onChange, ...other } = props

	return (
		<NumberFormat
			{...other}
			getInputRef={inputRef}
			onValueChange={(values) => {
				onChange({
					target: {
						value: values.value
					}
				})
			}}
			decimalScale={2}
			isNumericString
			prefix="$"
		/>
	)
}

const ChargeAccountDialog = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const selected = useSelector((state) => state.specific.households.selected)
	const facilities = useSelector((state) => state.specific.facilities.facilities) || []
	const selectedFacility = useSelector((state) => state.specific.facilities.selected)
	const { user, isSuperUser, facilityId: assignedFacilityId } = useCurrentUser()

	let { isShown, setIsShown } = props

	const [transactionTitle, setTransactionTitle] = useState('')
	const [amount, setAmount] = useState('')
	const [transactionCategory, setTransactionCategory] = useState('')
	const [facilityId, setFacilityId] = useState('')

	const [transactionTitleError, setTransactionTitleError] = useState(false)
	const [amountError, setAmountError] = useState(false)

	const { hasValidCardOnFile } = useSelectedHousehold()

	const chargeFacilities = useMemo(() => {
		return facilities
			.filter(
				(facility) =>
					facility.active !== false &&
					canAccessFacility({ roles: user?.roles, facilityId: assignedFacilityId }, facility._id)
			)
			.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
	}, [facilities, user?.roles, assignedFacilityId])

	const canChooseFacility = isSuperUser && chargeFacilities.length > 1
	const defaultFacilityId = selectedFacility?._id || assignedFacilityId || ''

	useEffect(() => {
		if (isShown) setFacilityId(defaultFacilityId)
	}, [isShown, defaultFacilityId])

	const addCharge = () => {
		if (!areFieldsValid()) {
			dispatch(showError('Please fix form errors.'))
		} else {
			let item = {
				description: transactionTitle,
				amount: Number(amount) * 100,
				type: 'Charge',
				household_id: selected._id,
				facility_id: canChooseFacility ? facilityId : defaultFacilityId
			}

			if (transactionCategory > '') {
				item.subtype = transactionCategory
			}

			dispatch(createItem(item)).then(() => {
				closeDialog()
			})
		}
	}

	const areFieldsValid = () => {
		setTransactionTitleError(!transactionTitle ? true : false)
		setAmountError(!amount ? true : false)

		const facilityReady = Boolean(canChooseFacility ? facilityId : defaultFacilityId)

		return Boolean(transactionTitle) && Boolean(amount) && facilityReady
	}

	const onTransactionTitleChange = (value) => {
		if (value) {
			if (transactionTitleError) setTransactionTitleError(false)
		}

		setTransactionTitle(value)
	}

	const onAmountChange = (value) => {
		if (value) {
			if (amountError) setAmountError(false)
		}

		setAmount(value)
	}

	const onTransactionCategoryChange = (e) => {
		setTransactionCategory(e?.target?.value || '')
	}

	const closeDialog = () => {
		setTransactionTitle('')
		setAmount('')
		setTransactionCategory('')
		setFacilityId('')
		setIsShown(false)
	}

	return (
		<Dialog
			open={isShown}
			aria-labelledby="form-dialog-title"
			className={classes.dialog}
			onClose={closeDialog}
			PaperProps={{
				style: {
					minWidth: '448px',
					maxWidth: '448px',
					width: '448px'
				}
			}}>
			<DialogTitle className={classes.title}>CHARGE ACCOUNT</DialogTitle>
			{hasValidCardOnFile ? (
				<DialogContent>
					<div className={classes.fieldWrapper}>
						<TextField
							label="Transaction Title"
							variant="filled"
							value={transactionTitle}
							onChange={(e) => onTransactionTitleChange(e.target.value)}
							error={transactionTitleError}
							helperText={transactionTitleError ? 'Please add a title.' : null}
						/>
					</div>
					<div className={classes.fieldWrapper}>
						<TextField
							label="Amount"
							variant="filled"
							value={amount}
							InputProps={{
								inputComponent: NumberFormatCustom
							}}
							onChange={(e) => onAmountChange(e.target.value)}
							error={amountError}
							helperText={amountError ? 'Please add an amount.' : null}
						/>
					</div>
					{canChooseFacility ? (
						<div className={classes.fieldWrapper}>
							<StyledSelect
								label="Facility"
								value={facilityId}
								onChange={(event) => setFacilityId(event.target.value)}>
								{chargeFacilities.map((facility) => (
									<MenuItem key={facility._id} value={facility._id}>
										{facility.name}
									</MenuItem>
								))}
							</StyledSelect>
						</div>
					) : null}
					<div className={classes.fieldWrapper}>
						<StyledSelect
							label="Category"
							value={transactionCategory}
							onChange={onTransactionCategoryChange}>
							{[
								['retail', 'Retail'],
								['other', 'Other']
							].map(([key, label]) => {
								return (
									<MenuItem key={key} value={key}>
										{label}
									</MenuItem>
								)
							})}
						</StyledSelect>
					</div>
				</DialogContent>
			) : (
				<DialogContent>
					<StyledAlert
						type="error"
						text="Client has no payment method on file. Add a payment method to the account and try again."
						style={{ marginBottom: 32 }}
					/>
				</DialogContent>
			)}
			<DialogActions className={classes.dialogActions}>
				<Button variant="contained" color="secondary" disabled={!hasValidCardOnFile} onClick={addCharge}>
					ADD CHARGE
				</Button>
				<Button variant="outlined" color="secondary" onClick={closeDialog}>
					CANCEL
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default ChargeAccountDialog
